import React from "react";
import styles from "./CoreWebVitalsViewer.module.css";
import { renderParagraphs, splitLines, stripBullet } from "./utils.jsx";

const SECTION_TITLES = ["Summary", "Mobile", "Desktop", "Pages", "Performance"];

const captureSections = (markdown = "") => {
  const regex = new RegExp(
    `^(Summary|Mobile|Desktop|Pages|Performance):\\s*([\\s\\S]*?)(?=^(?:Summary|Mobile|Desktop|Pages|Performance):|$)`,
    "gim"
  );
  const sections = {};
  let match;
  while ((match = regex.exec(markdown)) !== null) {
    sections[match[1].toLowerCase()] = match[2].trim();
  }
  return sections;
};

const CoreWebVitalsViewer = ({ markdown, title = "Core Web Vitals" }) => {
  if (!markdown) return null;

  const sections = captureSections(markdown);
  const hasStructuredData = Object.keys(sections).length > 0;

  if (!hasStructuredData) {
    return <div className={styles.card}>{renderParagraphs(markdown, styles)}</div>;
  }

  const summaryPoints = splitLines(sections.summary || "").map(stripBullet);
  const mobilePoints = splitLines(sections.mobile || "").map(stripBullet);
  const desktopPoints = splitLines(sections.desktop || "").map(stripBullet);
  const performancePoints = splitLines(sections.performance || "").map(stripBullet);
  const pages = splitLines(sections.pages || "").map(stripBullet);

  return (
    <div className={styles.card}>
      <div className={styles.hero}>
        <div className={styles.heroIcon}>CWV</div>
        <div>
          <h4 className={styles.heroTitle}>{title}</h4>
          <p className={styles.heroSubtitle}>
            A snapshot of how your experience feels on real devices.
          </p>
        </div>
      </div>

      {(mobilePoints.length > 0 || desktopPoints.length > 0) && (
        <div className={styles.deviceGrid}>
          {mobilePoints.length > 0 ? (
            <div className={styles.deviceCard}>
              <div className={styles.deviceHeader}>Mobile Experience</div>
              {mobilePoints.map((line, idx) => (
                <div className={styles.deviceRow} key={`mobile-${idx}`}>
                  <span className={styles.deviceDot} />
                  <span>{line}</span>
                </div>
              ))}
            </div>
          ) : null}
          {desktopPoints.length > 0 ? (
            <div className={styles.deviceCard}>
              <div className={styles.deviceHeader}>Desktop Experience</div>
              {desktopPoints.map((line, idx) => (
                <div className={styles.deviceRow} key={`desktop-${idx}`}>
                  <span className={styles.deviceDot} />
                  <span>{line}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {performancePoints.length > 0 ? (
        <div className={styles.performanceCard}>
          <div className={styles.sectionTitle}>Performance ideas</div>
          {performancePoints.map((line, idx) => (
            <div className={styles.deviceRow} key={`perf-${idx}`}>
              <span className={styles.deviceDot} />
              <span>{line}</span>
            </div>
          ))}
        </div>
      ) : null}

      {pages.length > 0 ? (
        <div className={styles.pagesCard}>
          <div className={styles.sectionTitle}>Pages</div>
          <ul className={styles.list}>
            {pages.map((page, idx) => (
              <li key={`page-${idx}`}>{page}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {summaryPoints.length > 0 ? (
        <div className={styles.summaryCard}>
          <div className={styles.sectionTitle}>Summary</div>
          {summaryPoints.map((line, idx) => (
            <div className={styles.summaryRow} key={`summary-${idx}`}>
              <span className={styles.summaryDot} />
              <span>{line}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default CoreWebVitalsViewer;
